import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  FolderKanban, FileText, Wrench, Users, DollarSign,
  ArrowRight, TrendingUp, Clock, AlertCircle, AlertTriangle,
  Package, Bell
} from 'lucide-react';

export default function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/dashboard/stats').then(r => {
      setStats(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-10 bg-gray-100 rounded-lg w-64 animate-pulse" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-32 bg-white rounded-xl border border-gray-100 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  const kpis = [
    {
      label: t('dashboard.activeProjects'), value: stats?.active_projects || 0,
      icon: FolderKanban, desc: t('dashboard.totalProjects', { count: stats?.total_projects || 0 }),
      featured: true, href: '/projects'
    },
    {
      label: t('dashboard.pendingEstimates'), value: stats?.pending_estimates || 0,
      icon: FileText, desc: t('dashboard.totalEstimates', { count: stats?.total_estimates || 0 }),
      color: 'text-amber-600', iconBg: 'bg-amber-50', href: '/estimates?status=sent'
    },
    {
      label: t('dashboard.activeJobs'), value: stats?.active_jobs || 0,
      icon: Wrench, desc: t('dashboard.completedJobs', { count: stats?.completed_jobs || 0 }),
      color: 'text-blue-600', iconBg: 'bg-blue-50', href: '/jobs?status=active'
    },
    {
      label: t('dashboard.revenue'), value: `$${(stats?.total_revenue || 0).toLocaleString()}`,
      icon: DollarSign, desc: t('dashboard.fromCompletedJobs'),
      color: 'text-emerald-600', iconBg: 'bg-emerald-50', href: '/invoices?status=paid'
    },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6 sm:space-y-8">
      {/* Welcome */}
      <div className="animate-fade-in-up">
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 font-['Barlow'] tracking-tight">
          {t('dashboard.welcome', { name: user?.first_name })}
        </h1>
        <p className="text-gray-500 mt-1 text-xs sm:text-sm">{t('dashboard.summary')}</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-5">
        {kpis.map((kpi, i) => {
          const Icon = kpi.icon;
          if (kpi.featured) {
            return (
              <Card
                key={i}
                data-testid={`kpi-card-${i}`}
                onClick={() => navigate(kpi.href)}
                className={`animate-fade-in-up delay-${(i+1)*75} col-span-2 sm:col-span-1 bg-gradient-to-br from-[#003366] to-[#004C99] border-0 shadow-lg shadow-[#003366]/15 hover:shadow-xl hover:shadow-[#003366]/20 transition-all duration-300 hover:-translate-y-0.5 cursor-pointer`}
              >
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-blue-200/80 font-medium">{kpi.label}</p>
                      <p className="text-2xl sm:text-3xl font-bold text-white mt-1 sm:mt-1.5 font-['Barlow']">{kpi.value}</p>
                      <p className="text-[10px] sm:text-xs text-blue-300/60 mt-1.5 sm:mt-2">{kpi.desc}</p>
                    </div>
                    <div className="w-9 h-9 sm:w-11 sm:h-11 rounded-xl bg-white/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 sm:w-5 sm:h-5 text-blue-200/70" strokeWidth={1.75} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          }
          return (
            <Card
              key={i}
              data-testid={`kpi-card-${i}`}
              onClick={() => navigate(kpi.href)}
              className={`animate-fade-in-up delay-${(i+1)*75} bg-white border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 cursor-pointer`}
            >
              <CardContent className="p-4 sm:p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-gray-500 font-medium">{kpi.label}</p>
                    <p className={`text-xl sm:text-3xl font-bold mt-1 sm:mt-1.5 font-['Barlow'] ${kpi.color || 'text-gray-900'}`}>{kpi.value}</p>
                    <p className="text-[10px] sm:text-xs text-gray-400 mt-1.5 sm:mt-2">{kpi.desc}</p>
                  </div>
                  <div className={`w-9 h-9 sm:w-11 sm:h-11 rounded-xl ${kpi.iconBg || 'bg-gray-50'} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 sm:w-5 sm:h-5 ${kpi.color || 'text-gray-400'}`} strokeWidth={1.75} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Three column lists: Estimates / Invoices / Jobs */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
        {/* Recent Estimates */}
        <Card className="animate-fade-in-up delay-300 bg-white border-gray-100 shadow-sm" data-testid="dashboard-recent-estimates">
          <CardHeader className="flex flex-row items-center justify-between pb-3 px-4 sm:px-6">
            <CardTitle className="text-sm sm:text-base font-semibold font-['Barlow'] flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#3399FF]" />
              {t('dashboard.recentEstimates')}
            </CardTitle>
            <Button
              data-testid="dashboard-recent-estimates"
              variant="ghost"
              size="sm"
              className="text-xs text-[#3399FF] hover:text-[#003366]"
              onClick={() => navigate('/estimates')}
            >
              {t('common.viewAll')} <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {!stats?.recent_estimates?.length ? (
              <div className="py-8 text-center text-sm text-gray-400">
                <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                {t('dashboard.noEstimatesYet')}
              </div>
            ) : (
              <div className="space-y-2">
                {stats.recent_estimates.map((est) => (
                  <div
                    key={est.id}
                    data-testid={`recent-estimate-${est.id}`}
                    // Fix C (2026-08-31): la fila abria la LISTA de estimados en vez
                    // del estimado tocado (Alian: "me lleva nada mas que a los
                    // estimados"). Reusa el mismo patron openEstimateId que ya usan
                    // GlobalSearch.jsx y las notificaciones del Header.
                    onClick={() => navigate('/estimates', { state: { openEstimateId: est.id } })}
                    className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-4 h-4 text-blue-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-[#003366]">{est.title}</p>
                        <p className="text-xs text-gray-400">{est.estimate_number}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-2">
                      <span className="text-xs sm:text-sm font-semibold text-gray-700">${(est.total || 0).toLocaleString()}</span>
                      <StatusBadge status={est.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Invoices */}
        <Card className="animate-fade-in-up delay-350 bg-white border-gray-100 shadow-sm" data-testid="dashboard-recent-invoices">
          <CardHeader className="flex flex-row items-center justify-between pb-3 px-4 sm:px-6">
            <CardTitle className="text-sm sm:text-base font-semibold font-['Barlow'] flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[#3399FF]" />
              {t('dashboard.recentInvoices', 'Recent Invoices')}
            </CardTitle>
            <Button
              data-testid="view-all-invoices-btn"
              variant="ghost"
              size="sm"
              className="text-xs text-[#3399FF] hover:text-[#003366]"
              onClick={() => navigate('/invoices')}
            >
              {t('common.viewAll')} <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {!stats?.recent_invoices?.length ? (
              <div className="py-8 text-center text-sm text-gray-400">
                <DollarSign className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                {t('dashboard.noInvoicesYet', 'No invoices yet')}
              </div>
            ) : (
              <div className="space-y-2">
                {stats.recent_invoices.map((inv) => (
                  <div
                    key={inv.id}
                    data-testid={`recent-invoice-${inv.id}`}
                    // Fix C (2026-08-31): abre ESE invoice, no la lista.
                    onClick={() => navigate('/invoices', { state: { openInvoiceId: inv.id } })}
                    className="flex items-center justify-between p-2.5 sm:p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center flex-shrink-0">
                        <DollarSign className="w-4 h-4 text-emerald-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-[#003366]">{inv.title || inv.invoice_number}</p>
                        <p className="text-xs text-gray-400">{inv.invoice_number}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 ml-2">
                      <span className="text-xs sm:text-sm font-semibold text-gray-700">${(inv.total || 0).toLocaleString()}</span>
                      <StatusBadge status={inv.status} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Upcoming Jobs */}
        <Card className="animate-fade-in-up delay-400 bg-white border-gray-100 shadow-sm" data-testid="dashboard-upcoming-jobs">
          <CardHeader className="flex flex-row items-center justify-between pb-3 px-4 sm:px-6">
            <CardTitle className="text-sm sm:text-base font-semibold font-['Barlow'] flex items-center gap-2">
              <Wrench className="w-4 h-4 text-[#3399FF]" />
              {t('dashboard.upcomingJobs')}
            </CardTitle>
            <Button
              data-testid="view-all-jobs-btn"
              variant="ghost"
              size="sm"
              className="text-xs text-[#3399FF] hover:text-[#003366]"
              onClick={() => navigate('/jobs')}
            >
              {t('common.viewAll')} <ArrowRight className="w-3 h-3 ml-1" />
            </Button>
          </CardHeader>
          <CardContent className="pt-0">
            {!stats?.upcoming_jobs?.length ? (
              <div className="py-8 text-center text-sm text-gray-400">
                <Wrench className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                {t('dashboard.noScheduledJobs')}
              </div>
            ) : (
              <div className="space-y-2">
                {stats.upcoming_jobs.map((job) => (
                  <div
                    key={job.id}
                    data-testid={`upcoming-job-${job.id}`}
                    // Fix C (2026-08-31): abre ESE trabajo, no la lista.
                    onClick={() => navigate(`/jobs/${job.id}`)}
                    className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
                        <Wrench className="w-4 h-4 text-indigo-500" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate group-hover:text-[#003366]">{job.title}</p>
                        <p className="text-xs text-gray-400">{job.job_number}</p>
                      </div>
                    </div>
                    <StatusBadge status={job.status} />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Maintenance Alerts */}
      {stats?.maintenance_alerts?.length > 0 && (
        <Card className="animate-fade-in-up delay-350 bg-amber-50/30 border-amber-200 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between pb-3 px-4 sm:px-6">
            <CardTitle className="text-sm sm:text-base font-semibold font-['Barlow'] flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              {t('dashboard.maintenanceAlerts')}
              <span className="text-[10px] font-medium bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full">{stats.maintenance_alerts.length}</span>
            </CardTitle>
            <div className="flex items-center gap-1.5">
              <Button
                data-testid="generate-maint-notifications-btn"
                variant="ghost"
                size="sm"
                className="text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-100/50"
                onClick={async () => {
                  try {
                    const res = await api.post('/equipment/maintenance/generate-notifications');
                    toast.success(t('dashboard.notificationsGenerated', { count: res.data.count }));
                  } catch { toast.error('Error'); }
                }}
              >
                <Bell className="w-3 h-3 mr-1" /> {t('dashboard.generateNotifications')}
              </Button>
              <Button
                data-testid="view-all-equipment-btn"
                variant="ghost"
                size="sm"
                className="text-xs text-[#3399FF] hover:text-[#003366]"
                onClick={() => navigate('/equipment')}
              >
                {t('dashboard.viewEquipment')} <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {stats.maintenance_alerts.map((alert) => (
                <div
                  key={alert.id}
                  data-testid={`maint-alert-${alert.id}`}
                  onClick={() => navigate('/equipment')}
                  className="flex items-center gap-2.5 p-2.5 rounded-lg bg-white border border-amber-100 hover:bg-amber-50/50 transition-colors cursor-pointer group"
                >
                  <div className="w-8 h-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <Wrench className="w-4 h-4 text-amber-600" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 truncate group-hover:text-amber-700">{alert.name}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {alert.serial_number && <span className="font-mono">{alert.serial_number}</span>}
                      {alert.next_maintenance_date && <span> · {alert.next_maintenance_date}</span>}
                    </p>
                  </div>
                  <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick actions */}
      <Card className="animate-fade-in-up delay-400 bg-white border-gray-100 shadow-sm">
        <CardContent className="p-4 sm:p-6">
          <h3 className="text-sm sm:text-base font-semibold font-['Barlow'] mb-3 sm:mb-4 text-gray-700">{t('dashboard.quickActions')}</h3>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button
              data-testid="quick-new-project"
              onClick={() => navigate('/projects')}
              size="sm"
              className="bg-[#003366] hover:bg-[#004080] text-white shadow-sm text-xs sm:text-sm"
            >
              <FolderKanban className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" /> {t('dashboard.newProject')}
            </Button>
            <Button
              data-testid="quick-new-estimate"
              onClick={() => navigate('/estimates')}
              variant="outline"
              size="sm"
              className="border-gray-200 text-xs sm:text-sm"
            >
              <FileText className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" /> {t('dashboard.newEstimate')}
            </Button>
            <Button
              data-testid="quick-new-contact"
              onClick={() => navigate('/contacts')}
              variant="outline"
              size="sm"
              className="border-gray-200 text-xs sm:text-sm"
            >
              <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" /> {t('dashboard.newContact')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
